import { Unsubscriber } from '@/core/event-bus';
import PlayerStateRepository, { PlayerState, StateHandler } from '@/core/player-state';

export type PlaybackInfo = {
    stuckCount: number
    stuckDiff: number
    stuckDurationDiff: number
}

class PlaybackTrackerModel {

    private stuckCount = 0;

    private lastStuckCount = 0;

    private stuckDuration = 0;

    private listeners: Unsubscriber[] = [];

    private lastSnapshotTimestamp = -Infinity;

    constructor(
        private playerStateRepository: PlayerStateRepository
    ) {
        this.listeners.push(
            this.playerStateRepository.onStateChange(
                this.onStateChange.bind(this) as StateHandler
            ).unsubscriber
        );
    }

    public dispose() {
        for (const listener of this.listeners) {
            listener();
        }
    }

    private onStateChange(state: Partial<PlayerState>, previousState: PlayerState) {
        if (!state.hasOwnProperty('playback')) return;

        const isStuck = state.playback?.isStuck;
        const wasStuck = previousState.playback?.isStuck;

        if (
            isStuck &&
            wasStuck === false
        ) {
            this.stuckCount++;
        } else if (
            isStuck === false &&
            wasStuck
        ) {
            this.stuckDuration += this.calculateStuckDuration(previousState);
        }
    }

    private calculateStuckDuration(state: PlayerState) {
        const startTime = Math.max(
            state.playback?.stuckTimestamp ?? -Infinity,
            this.lastSnapshotTimestamp
        );

        return Date.now() - startTime;
    }

    // getters and setters

    public get snapshot(): PlaybackInfo {
        const stuckDiff = this.stuckCount - this.lastStuckCount;
        const stuckDurationDiff = this.state.playback?.isStuck
            ? this.stuckDuration + this.calculateStuckDuration(this.state)
            : this.stuckDuration;

        this.lastStuckCount = this.stuckCount;
        this.lastSnapshotTimestamp = Date.now();
        this.stuckDuration = 0;

        return {
            stuckCount: this.stuckCount,
            stuckDiff,
            stuckDurationDiff: Math.ceil(stuckDurationDiff / 1000) // convert to seconds
        } as PlaybackInfo;
    }

    private get state(): PlayerState {
        return this.playerStateRepository.state;
    }
}

function* playbackTrackerGenerator(
    playbackTrackerModel: PlaybackTrackerModel
) {
    try {
        while (true) {
            yield playbackTrackerModel.snapshot;
        }
    } finally {
        playbackTrackerModel.dispose();
    }
}

export default function playbackStuckTracker(
    playerStateRepository: PlayerStateRepository
): Generator {

    const playbackTrackerModel = new PlaybackTrackerModel(
        playerStateRepository
    );

    return playbackTrackerGenerator(playbackTrackerModel);
}
